import {
  PaymentFeeKind,
  type FeeScope,
  type PaymentFee,
  type ProviderRailRoute,
} from "../types/paymentFees.types";
import {
  corridorScopeOf,
  feeQueryFor,
  feeScopeOf,
  removalImpact,
  resolveFee,
  routesCoveredBy,
  sameScope,
  scopeKey,
  type RemovalImpact,
} from "./feeMath";

export interface PriceEditTarget {
  exactScope: FeeScope;
  effective: PaymentFee | null;
  /** General scope only: the rule pricing this corridor today also prices these other corridors. */
  broad: { scope: FeeScope; routes: ProviderRailRoute[] } | null;
}

export function priceEditTarget(
  route: ProviderRailRoute,
  feeKind: PaymentFeeKind,
  organizationId: string | null,
  fees: PaymentFee[],
  routes: ProviderRailRoute[],
  now: number,
): PriceEditTarget {
  const isSpecial = feeKind === PaymentFeeKind.COLOCA_COMMISSION && organizationId != null;
  // A special price always pins the whole corridor: specificity is a column count, so an organization rule
  // with an open column loses to any general rule that fills it.
  const exactScope = corridorScopeOf(route, feeKind, isSpecial ? organizationId : null);
  const effective = resolveFee(feeQueryFor(route, feeKind, isSpecial ? organizationId : null), fees, now);

  if (isSpecial || !effective || sameScope(feeScopeOf(effective), exactScope)) {
    return { exactScope, effective, broad: null };
  }

  const scope = feeScopeOf(effective);
  const key = scopeKey(feeKind, scope);
  const winsOn = routesCoveredBy(feeKind, scope, routes).filter((candidate) => {
    const winner = resolveFee(feeQueryFor(candidate, feeKind), fees, now);
    return winner != null && scopeKey(winner.fee_kind, feeScopeOf(winner)) === key;
  });
  return { exactScope, effective, broad: { scope, routes: winsOn } };
}

export interface SpecialPriceRemovalPlan {
  scope: FeeScope;
  /** Deletion order: the version in force goes last. */
  rows: PaymentFee[];
  impact: RemovalImpact[];
}

export function specialPriceRemovalPlan(
  route: ProviderRailRoute,
  organizationId: string,
  fees: PaymentFee[],
  routes: ProviderRailRoute[],
  now: number,
): SpecialPriceRemovalPlan | null {
  const winner = resolveFee(feeQueryFor(route, PaymentFeeKind.COLOCA_COMMISSION, organizationId), fees, now);
  if (!winner || winner.organization_id !== organizationId) return null;

  // Old versions are never closed, so deleting only the one in force would bring the previous one back.
  const scope = feeScopeOf(winner);
  const key = scopeKey(PaymentFeeKind.COLOCA_COMMISSION, scope);
  const versions = fees.filter((fee) => !fee.deleted_at && scopeKey(fee.fee_kind, feeScopeOf(fee)) === key);
  const rows = [
    ...versions
      .filter((fee) => fee.id !== winner.id)
      .sort((a, b) => Date.parse(b.valid_from) - Date.parse(a.valid_from)),
    winner,
  ];
  return { scope, rows, impact: removalImpact(versions, fees, routes, now) };
}

export function organizationsKeepingOwnPrice(
  routes: ProviderRailRoute[],
  fees: PaymentFee[],
  now: number,
): string[] {
  const organizationIds = new Set<string>();
  for (const fee of fees) {
    if (fee.fee_kind === PaymentFeeKind.COLOCA_COMMISSION && fee.organization_id) {
      organizationIds.add(fee.organization_id);
    }
  }
  return [...organizationIds].filter((organizationId) =>
    routes.some(
      (route) =>
        resolveFee(feeQueryFor(route, PaymentFeeKind.COLOCA_COMMISSION, organizationId), fees, now)
          ?.organization_id === organizationId,
    ),
  );
}
